import { useEffect, useMemo, useRef, useState } from "react";
import useStore from "../../../../useStore";
import { useAuthentication } from "../../../../Hooks/useAuthentication";
import CustomCalendar from "../../../Elements/Calendar/Calendar";
import { useToast } from "../../../../Contexts/Toast/toast.provider";
import { fmtCurrency } from "../../../../Utilities/Moneda/moneda";
import PagoSwitch from "../../../Elements/PagoSwitch/PagoSwitch";
import "./AdminCourierPayments.css";

const toDateInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtRangoCorto = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

function buildWhatsAppUrl(phone, courierName, monto, rangoLabel) {
  if (!phone) return null;
  const clean = String(phone).replace(/[\s\-().]/g, "");
  const normalized = clean.startsWith("+") ? clean : `+52${clean}`;
  const message = `Hola ${courierName || ""}, te contactamos de BubUp: te corresponde ${fmtCurrency(monto)} por tus entregas pagadas con Mercado Pago durante la semana del ${rangoLabel}. En breve te hacemos la transferencia. 🙌`;
  return `https://wa.me/${normalized.replace("+", "")}?text=${encodeURIComponent(message)}`;
}

export default function AdminCourierPayments() {
  const showToast = useToast();
  const { role } = useAuthentication();
  const isAdmin = ["admin", "administrador", "administrator"].includes(
    String(role || "").toLowerCase()
  );

  const {
    adminCourierWeeklyEarnings,
    loadingAdminCourierWeeklyEarnings,
    errorAdminCourierWeeklyEarnings,
    fetchDataAdminCourierWeeklyEarnings,
    setWeeklyPaymentStatus,
  } = useStore();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [q, setQ] = useState("");

  const refDate = useMemo(() => toDateInput(selectedDate), [selectedDate]);

  // Siempre apunta a la semana que está seleccionada AHORA MISMO — para que un
  // guardado que arrancó en una semana vieja no pise en pantalla una semana más
  // nueva a la que el admin ya cambió mientras esperaba la respuesta.
  const refDateRef = useRef(refDate);
  useEffect(() => {
    refDateRef.current = refDate;
  }, [refDate]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchDataAdminCourierWeeklyEarnings(refDate);
  }, [isAdmin, refDate, fetchDataAdminCourierWeeklyEarnings]);

  useEffect(() => {
    if (errorAdminCourierWeeklyEarnings) showToast("error", errorAdminCourierWeeklyEarnings);
  }, [errorAdminCourierWeeklyEarnings, showToast]);

  const rango = adminCourierWeeklyEarnings?.rango;
  const rangoLabel = useMemo(() => {
    if (!rango) return "";
    return `${fmtRangoCorto(rango.inicio)} – ${fmtRangoCorto(rango.fin)}`;
  }, [rango]);

  // La semana ya terminó → los números quedaron congelados, recién ahí es seguro
  // marcar el pago (si no, podría entrar más venta/deuda a esta misma semana después).
  const weekEnded = !!rango?.fin && new Date() >= new Date(rango.fin);

  const handleSavePago = async (tipo, courierId, { pagado, pagadoAt }) => {
    const res = await setWeeklyPaymentStatus({
      tipo,
      entidadId: courierId,
      pagado,
      pagadoAt,
      date: refDate,
    });
    if (res?.statusCode === 200) {
      showToast("success", pagado ? "Pago marcado ✅" : "Pago desmarcado");
      // Refresca la semana que se está viendo AHORA (puede haber cambiado mientras
      // se guardaba), no la que estaba seleccionada cuando arrancó el guardado.
      fetchDataAdminCourierWeeklyEarnings(refDateRef.current);
    } else {
      showToast("error", res?.message || "No se pudo actualizar el estado de pago");
    }
  };

  const repartidoresFiltrados = useMemo(() => {
    const list = adminCourierWeeklyEarnings?.repartidores || [];
    const query = q.trim().toLowerCase();
    if (!query) return list;
    return list.filter((r) => (r.courierName || "").toLowerCase().includes(query));
  }, [adminCourierWeeklyEarnings, q]);

  if (!isAdmin) {
    return (
      <div className="container-fluid container-mh acp-page">
        <div className="alert alert-danger">Esta sección es solo para administradores.</div>
      </div>
    );
  }

  const totales = adminCourierWeeklyEarnings?.totalesGlobales;

  return (
    <div className="container-fluid container-mh acp-page">
      <div className="row mb-3">
        <div className="col">
          <p className="acp-subtext">
            Elige un día en el calendario para ver esa semana. <strong>"Por pagar"</strong> es lo que el admin le
            debe transferir al repartidor por entregas en Mercado Pago. <strong>"Le debe al admin"</strong> es la
            comisión de envío en efectivo que el repartidor trae en la mano por pedidos pagados en efectivo — la
            parte del negocio ya se la pagó al recogerlo, así que no le queda debiendo nada al negocio.
            Los switches de pago solo se habilitan cuando la semana ya terminó — así los números
            quedan congelados y no puede marcarse pagado algo que después siga creciendo.
          </p>
          <div className="mb-3">
            <CustomCalendar value={selectedDate} onChange={setSelectedDate} />
          </div>
        </div>
      </div>

      {loadingAdminCourierWeeklyEarnings && (
        <div className="text-center py-4 text-muted">Cargando reporte…</div>
      )}

      {!loadingAdminCourierWeeklyEarnings && !!errorAdminCourierWeeklyEarnings && (
        <div className="alert alert-danger">{errorAdminCourierWeeklyEarnings}</div>
      )}

      {!loadingAdminCourierWeeklyEarnings && !errorAdminCourierWeeklyEarnings && adminCourierWeeklyEarnings && (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <span className="acp-rango">Semana: {rangoLabel}</span>
            <span className="acp-count">
              {totales?.repartidoresConActividad || 0} repartidor(es) con actividad · {totales?.entregas || 0} entrega(s)
            </span>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-6 col-md-6">
              <div className="acp-stat acp-stat--pay">
                <span className="acp-stat-label">Por pagar (MP)</span>
                <span className="acp-stat-value">{fmtCurrency(totales?.porPagar)}</span>
              </div>
            </div>
            <div className="col-6 col-md-6">
              <div className="acp-stat acp-stat--admin">
                <span className="acp-stat-label">Le deben al admin (envío efvo.)</span>
                <span className="acp-stat-value">{fmtCurrency(totales?.leDebeAlAdmin)}</span>
              </div>
            </div>
          </div>

          {totales?.pendienteConfirmacionMp > 0 && (
            <div className="alert alert-warning py-2 px-3 mb-3 small">
              ⏳ {fmtCurrency(totales.pendienteConfirmacionMp)} en entregas de Mercado Pago sin confirmar todavía —
              no cuentan como "por pagar" hasta que Mercado Pago confirme el pago.
            </div>
          )}

          <input
            type="text"
            className="form-control mb-3 acp-search"
            placeholder="Buscar repartidor…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          {repartidoresFiltrados.length === 0 ? (
            <div className="alert alert-warning text-center">⚠️ Ningún repartidor tuvo entregas esta semana.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle acp-table">
                <thead>
                  <tr>
                    <th>Repartidor</th>
                    <th>Entregas</th>
                    <th>Por pagar (MP)</th>
                    <th>¿Ya le pagué?</th>
                    <th>Ya cobrado (efvo.)</th>
                    <th>Le debe al admin</th>
                    <th>¿Ya me pagó?</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {repartidoresFiltrados.map((r) => {
                    const waUrl = buildWhatsAppUrl(r.phone, r.courierName, r.porPagar, rangoLabel);
                    const debeAlgo = r.leDebeAlAdmin > 0;
                    return (
                      <tr key={r.courierId}>
                        <td>
                          <strong>{r.courierName || "Sin nombre"}</strong>
                          {r.phone && <div className="small text-muted">{r.phone}</div>}
                        </td>
                        <td>
                          {r.entregas}
                          {r.abandonados > 0 && (
                            <div className="small text-muted">{r.abandonados} no entregado(s)</div>
                          )}
                        </td>
                        <td>
                          <strong className={r.porPagar > 0 ? "acp-porpagar" : ""}>
                            {fmtCurrency(r.porPagar)}
                          </strong>
                        </td>
                        <td>
                          {r.porPagar > 0 ? (
                            <PagoSwitch
                              pagado={r.pagado}
                              pagadoAt={r.pagadoAt}
                              locked={!weekEnded}
                              onSave={(v) => handleSavePago("repartidor", r.courierId, v)}
                            />
                          ) : (
                            <span className="text-muted small">— nada que pagar</span>
                          )}
                        </td>
                        <td>{fmtCurrency(r.cobradoEnEfectivo)}</td>
                        <td className={debeAlgo ? "acp-debe" : ""}>{fmtCurrency(r.leDebeAlAdmin)}</td>
                        <td>
                          {r.leDebeAlAdmin > 0 ? (
                            <PagoSwitch
                              pagado={r.deudaPagada}
                              pagadoAt={r.deudaPagadaAt}
                              locked={!weekEnded}
                              onSave={(v) => handleSavePago("repartidorDebtAdmin", r.courierId, v)}
                            />
                          ) : (
                            <span className="text-muted small">— no debe nada</span>
                          )}
                        </td>
                        <td className="text-end">
                          {waUrl && r.porPagar > 0 && (
                            <a
                              href={waUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-success"
                            >
                              WhatsApp
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
